import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { getWorkSubtasks, deleteWork, updateWork, createWork, setWorkDone } from "../../../../api/hub/work";
import { useToast } from "../../../../contexts/ToastContext";

// ─── SubtaskSection ─────────────────────────────────────────────────────────────
// Self-contained subtask list for a parent task. Replaces the old SubtaskList
// (slider) and the inline subtask rendering in InboxRow with a single shared
// component. Fetches its own data via fn_work_subtasks on mount, owns its CRUD
// state, and signals count changes back to the parent through onCountChange so
// the parent can update the subtask badge in its cache.
//
// Props:
//   parentId       (uuid, required) — the parent task whose subtasks to show
//   onCountChange  (fn, required)   — called with the new array length after
//                                     every create or delete so the parent can
//                                     update the subtask count in its cache
//
// One-level nesting (spec §5): this component is only rendered for top-level
// tasks (the parent decides). It does not gate on parent_task_id itself.
//
// PART A covers: fetch on mount, list rendering, and delete (confirm-on-second-
// click). Add (inline textbox) and rename (click-to-edit) are added in later
// PARTs — the state/handler structure below is laid out so they slot in cleanly.
const SubtaskSection = ({ parentId, onCountChange, onTaskUpdated, compact, revision }) => {
  const [subtasks, setSubtasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ─── In-component refresh (Item 1) ──────────────────────────────────────
  // Instead of remounting SubtaskSection via a `key` change (which made the
  // slider disappear briefly), the parent bumps `revision` after a
  // create/delete so this instance re-fetches in place. `refreshing` toggles a
  // thin loading bar at the top while the re-fetch runs; the existing list
  // stays visible underneath (no disappearing/remounting).
  const [refreshing, setRefreshing] = useState(false);

  // Per-row delete state. deletingId holds the subtask id currently armed for
  // confirm (first click); the second click proceeds. deleteErrors maps
  // subtask id → refusal message from fn_work_delete (effort logged / open
  // commitment), shown inline beneath the row.
  const [deletingId, setDeletingId] = useState(null);
  const [deleteErrors, setDeleteErrors] = useState({});

  // ─── Inline rename (click-to-edit title) ───────────────────────────────
  // editingId holds the subtask currently being renamed; editingTitle is the
  // draft text. cancelBlurRef prevents Escape's blur from triggering a save.
  // renameErrors maps subtask id → update failure message (shown inline).
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const cancelBlurRef = useRef(false);
  const [renameErrors, setRenameErrors] = useState({});
  // The edit input element ref. useLayoutEffect focuses + selects the input
  // once when editingId changes to a truthy value — NOT a callback ref, which
  // React re-invokes every render (re-focus + re-select-all after each
  // keystroke, overwriting the character the user just typed).
  const editInputRef = useRef(null);

  // ─── Done toggle (dot) ───────────────────────────────────────────────────
  // Toggling a subtask's dot calls fn_work_set_done on the backend. doneErrors
  // maps subtask id → set-done failure message (shown inline beneath the row,
  // same slot as deleteErrors/renameErrors). The optimistic flip is reverted on
  // error so the dot returns to its previous state.
  const [doneErrors, setDoneErrors] = useState({});

  // ─── Bulk selection (checkbox) ────────────────────────────────────────
  // Local selection state for subtasks. No parent (TaskSlideOut/WorkPanel)
  // exposes a bulk-action handler for subtasks — useWorkBulkActions only
  // covers top-level tasks — so selection is owned here for now and can be
  // wired to bulk actions in a future session. selectedSubtaskIds is an
  // array of subtask ids; toggleSubtaskSelect flips membership of one id.
  const [selectedSubtaskIds, setSelectedSubtaskIds] = useState([]);
  const toggleSubtaskSelect = (id) => {
    setSelectedSubtaskIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const isSubtaskSelected = (id) => selectedSubtaskIds.includes(id);

  // ─── Bulk delete (work-page-fixes Item 2) ──────────────────────────────
  // useToast drives the bottom-center toast (same as WorkPanel's repeat
  // toasts). bulkDeleteArmed drives the confirm-on-second-click Delete
  // button (first click → "Delete?" in danger red, second click → execute).
  // bulkDeleting guards the button while the deletions are in flight. The
  // armed state resets whenever the selection empties so the next selection
  // starts from the plain "Delete" label.
  const { toastSuccess, toastError } = useToast();
  const [bulkDeleteArmed, setBulkDeleteArmed] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  useEffect(() => {
    if (selectedSubtaskIds.length === 0) setBulkDeleteArmed(false);
  }, [selectedSubtaskIds.length]);

  // ─── Inline "+ Add step" row ──────────────────────────────────────────
  // Quick-capture input at the bottom of the subtask list. Enter or the +
  // button submits the title to createWork({ title, parent_task_id: parentId }).
  // On success the new subtask is appended to the local array (and
  // onCountChange signals the new count to the parent), the input clears and
  // re-focuses for rapid entry. On error the message shows inline below.
  const [newStepTitle, setNewStepTitle] = useState("");
  const newStepRef = useRef(null);
  const [newStepError, setNewStepError] = useState("");
  const [submittingStep, setSubmittingStep] = useState(false);

  // ─── Fetch on mount (and when parentId changes) ───────────────────────────
  // fn_work_subtasks returns the subtask array for the parent. The component
  // owns the array — no parent-side cache to invalidate. A parentId change
  // (opening a different task in the slider) re-fetches.
  useEffect(() => {
    if (!parentId) { setSubtasks([]); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setError("");
    getWorkSubtasks(parentId)
      .then((resp) => {
        if (cancelled) return;
        setSubtasks(Array.isArray(resp?.data) ? resp.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || "Failed to load subtasks");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [parentId]);

  // ─── Re-fetch on revision change (Item 1) ────────────────────────────────
  // The parent bumps `revision` (instead of changing the `key`) after a
  // subtask is created/deleted elsewhere. We re-fetch in place — the existing
  // list stays rendered while `refreshing` shows a thin top loading bar.
  // Guard: skip the initial mount (revision undefined / 0) since the fetch-on-
  // mount effect above already loads the subtasks. A `cancelled` flag guards
  // against a rapid second bump writing stale data into state.
  useEffect(() => {
    if (!revision || revision <= 0) return;
    if (!parentId) return;
    let cancelled = false;
    setRefreshing(true);
    getWorkSubtasks(parentId)
      .then((resp) => {
        if (cancelled) return;
        setSubtasks(Array.isArray(resp?.data) ? resp.data : []);
        setError("");
      })
      .catch((e) => {
        if (cancelled) return;
        // Keep the existing list on refresh failure; surface the message via
        // the shared error state so it renders inline like a load error.
        setError(e?.message || "Failed to refresh subtasks");
      })
      .finally(() => {
        if (cancelled) return;
        setRefreshing(false);
      });
    return () => { cancelled = true; };
  }, [revision]);

  // ─── Delete (confirm-on-second-click) ────────────────────────────────────
  // First click arms confirm for this row (clearing any prior refusal message).
  // Second click calls fn_work_delete — a soft delete that REFUSES (raises) when
  // effort is logged or an open commitment points at the work. On success the
  // row is removed from the local array and onCountChange is called with the
  // new length so the parent can update its badge. On refusal the raised message
  // is surfaced inline beneath the row.
  const handleDeleteClick = async (st) => {
    if (deletingId !== st.id) {
      setDeletingId(st.id);
      setDeleteErrors((prev) => ({ ...prev, [st.id]: undefined }));
      return;
    }
    setDeletingId(null);
    try {
      await deleteWork(st.id, "Deleted by user");
      setSubtasks((prev) => {
        const next = prev.filter((s) => s.id !== st.id);
        onCountChange?.(next.length);
        return next;
      });
      setDeleteErrors((prev) => ({ ...prev, [st.id]: undefined }));
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Delete failed";
      setDeleteErrors((prev) => ({ ...prev, [st.id]: msg }));
    }
  };

  // ─── Bulk delete handler (work-page-fixes Item 2) ──────────────────────
  // Loops through selectedSubtaskIds and calls deleteWork for each (the same
  // handler the per-row trash icon uses). Successfully deleted subtasks are
  // removed from the local array and onCountChange reports the new length;
  // failed ones stay visible and remain selected so the user can retry. A
  // toast reports the outcome: toastSuccess("{N} subtasks deleted") when all
  // succeed, or toastError with the first error message when any fail.
  const handleBulkDelete = async () => {
    if (bulkDeleting) return;
    setBulkDeleting(true);
    const ids = [...selectedSubtaskIds];
    const failed = [];
    let firstError = "";
    await Promise.all(
      ids.map(async (id) => {
        try {
          await deleteWork(id, "Deleted by user");
        } catch (e) {
          const msg = e?.response?.data?.message || e?.message || "Delete failed";
          if (!firstError) firstError = msg;
          failed.push(id);
        }
      })
    );
    const succeeded = ids.filter((id) => !failed.includes(id));
    if (succeeded.length > 0) {
      setSubtasks((prev) => {
        const next = prev.filter((s) => !succeeded.includes(s.id));
        onCountChange?.(next.length);
        return next;
      });
    }
    setSelectedSubtaskIds(failed);
    setBulkDeleteArmed(false);
    if (failed.length > 0) {
      toastError(firstError || "Some subtasks could not be deleted");
    } else {
      toastSuccess(`${succeeded.length} subtask${succeeded.length !== 1 ? "s" : ""} deleted`);
    }
    setBulkDeleting(false);
  };

  // ─── Focus fix (useLayoutEffect, not a callback ref) ──────────────────
  // When editingId changes to a truthy value, find the input element once and
  // focus + select-all. useLayoutEffect runs synchronously after DOM mutation
  // but before paint, so the focus lands before the browser paints the input
  // — no flicker. A callback ref would be recreated every render (re-firing
  // focus + select on each keystroke, overwriting the just-typed character);
  // this approach fires exactly once per edit-session start.
  useLayoutEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // ─── Inline rename handlers ────────────────────────────────────────────
  // Click the title → swap to a pre-filled input. Enter / blur saves, Escape
  // cancels. On save we call fn_work_update (via updateWork) with the new
  // title; on success the local title is updated, on error it reverts and the
  // message is shown inline. stopPropagation on the title click and input
  // keydown keeps them from bubbling to parent row click handlers.
  const handleStartEdit = (st) => {
    setEditingId(st.id);
    setEditingTitle(st.title || "");
    setRenameErrors((prev) => ({ ...prev, [st.id]: undefined }));
    cancelBlurRef.current = false;
  };

  const handleCancelEdit = () => {
    // Set the guard before clearing editingId — the unmount blur sees it and
    // skips the save.
    cancelBlurRef.current = true;
    setEditingId(null);
    setEditingTitle("");
  };

  const handleSaveEdit = async (st) => {
    const next = editingTitle.trim();
    const original = st.title || "";
    // Empty or unchanged → just close the editor (no API call).
    if (!next || next === original) {
      setEditingId(null);
      setEditingTitle("");
      return;
    }
    try {
      await updateWork({ task_id: st.id, title: next });
      setSubtasks((prev) =>
        prev.map((s) => (s.id === st.id ? { ...s, title: next } : s))
      );
      setRenameErrors((prev) => ({ ...prev, [st.id]: undefined }));
    } catch (e) {
      // Revert to the original title; surface the message inline.
      const msg = e?.response?.data?.message || e?.message || "Rename failed";
      setRenameErrors((prev) => ({ ...prev, [st.id]: msg }));
    }
    setEditingId(null);
    setEditingTitle("");
  };

  const handleEditKeyDown = (e, st) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      handleSaveEdit(st);
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      handleCancelEdit();
    }
  };

  const handleEditBlur = (st) => {
    // Escape sets cancelBlurRef so this blur doesn't save.
    if (cancelBlurRef.current) {
      cancelBlurRef.current = false;
      return;
    }
    handleSaveEdit(st);
  };

  // ─── Inline add-step handlers ──────────────────────────────────────────
  // Enter or + click calls createWork with the trimmed title + parentId. Empty
  // or whitespace-only is a no-op (guard at the top). On success the new subtask
  // appends to the local array, onCountChange signals the new length, the input
  // clears, and the input re-focuses for rapid entry. On error the message
  // shows inline below the row.
  const submitNewStep = async () => {
    const title = newStepTitle.trim();
    if (!title || submittingStep) return;
    setSubmittingStep(true);
    setNewStepError("");
    try {
      const resp = await createWork({ title, parent_task_id: parentId });
      const created = resp?.data;
      const row = Array.isArray(created) ? created[0] : created;
      if (row?.id) {
        const item = { id: row.id, title: row.title || title, completed_at: row.completed_at || null };
        setSubtasks((prev) => {
          const next = [...prev, item];
          onCountChange?.(next.length);
          return next;
        });
      } else {
        // Response shape didn't include a parseable id — bump the count so the
        // parent remounts + refetches (same soft refresh as delete).
        onCountChange?.(subtasks.length + 1);
      }
      setNewStepTitle("");
      // Re-focus for rapid entry of the next subtask.
      if (newStepRef.current) newStepRef.current.focus();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Create failed";
      setNewStepError(msg);
    } finally {
      setSubmittingStep(false);
    }
  };

  const handleNewStepKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitNewStep();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setNewStepTitle("");
      setNewStepError("");
    }
  };

  // ─── Subtask progress bar (Item 14) ────────────────────────────────────
  // Completion ratio for the thin progress bar rendered below the heading.
  // A subtask is "done" when its `done` flag is true (fn_work_subtasks returns
  // this directly). The backend sets/clears `completed_at` alongside the flag,
  // so `completed_at` is kept in sync as a fallback for rows fetched before the
  // `done` field shipped.
  const total = subtasks.length;
  const completed = subtasks.filter((s) => s.done || !!s.completed_at).length;
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  // ─── Done toggle handler (Item 15) ────────────────────────────────────
  // Clicking a subtask's dot flips its `done` flag via fn_work_set_done.
  // Optimistic: flip the local flag + completed_at immediately for a snappy
  // dot color change. On success, confirm the final state from the response (it
  // carries the backend's completed_at) and notify the parent via onTaskUpdated
  // (the parent task's status may have changed — done→finished, undone→first
  // open). On error, revert the row to its previous state and show the message.
  const handleToggleDone = async (st) => {
    const prevDone = !!(st.done || st.completed_at);
    const nextDone = !prevDone;
    setDoneErrors((p) => {
      const n = { ...p };
      delete n[st.id];
      return n;
    });
    setSubtasks((prev) =>
      prev.map((s) =>
        s.id === st.id
          ? { ...s, done: nextDone, completed_at: nextDone ? new Date().toISOString() : null }
          : s
      )
    );
    try {
      const resp = await setWorkDone(st.id, nextDone);
      // fn_work_set_done returns the updated task row; prefer its completed_at
      // when present so the local state matches the backend exactly.
      const row = resp?.data;
      if (row && typeof row === "object" && row.completed_at != null) {
        setSubtasks((prev) =>
          prev.map((s) =>
            s.id === st.id
              ? { ...s, done: row.done != null ? !!row.done : nextDone, completed_at: row.completed_at }
              : s
          )
        );
      }
      // The parent task's status may have shifted (all-done → finished, or one
      // undone → first open). Ask the parent to refresh its task detail.
      onTaskUpdated?.();
    } catch (e) {
      // Revert: restore the previous done/completed_at for this row.
      setSubtasks((prev) =>
        prev.map((s) =>
          s.id === st.id
            ? { ...s, done: prevDone, completed_at: st.completed_at || null }
            : s
        )
      );
      setDoneErrors((p) => ({ ...p, [st.id]: e?.message || "Could not update step." }));
    }
  };

  return (
    <div className={compact ? "pl-1" : "px-5 py-3 border-b border-gray-200 dark:border-gray-800"}>
      {/* ─── Refresh loading bar (Item 1) ────────────────────────────────
          Thin pulse bar that fades in/out via opacity while a revision-
          triggered re-fetch is in progress (always rendered so the
          transition-opacity fade works). Same palette as the search loading
          bar in TaskListView (h-0.5 bg-blue-500 animate-pulse). The subtask
          list below stays rendered the whole time — nothing is hidden during
          refresh. */}
      <div className={`h-0.5 w-full transition-opacity ${refreshing ? "opacity-100" : "opacity-0"}`}>
        <div className="h-full bg-blue-500 animate-pulse rounded-full" />
      </div>
      <p className={`text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2 ${compact ? "pl-1" : ""}`}>
        Subtasks
        {subtasks.length > 0 && (
          <span className="ml-2 font-normal text-gray-400 dark:text-gray-500">
            {subtasks.length} step{subtasks.length !== 1 ? "s" : ""}
          </span>
        )}
      </p>

      {/* ─── Subtask progress bar (Item 14) ───────────────────────────────
          Thin completion-ratio bar. Only rendered when there is at least one
          subtask; hidden entirely when the list is empty. Re-renders on every
          local state mutation (create appends, delete filters, completion
          toggles via the backend's completed_at field). */}
      {total > 0 && (
        <div className={`flex items-center gap-2 mb-2 ${compact ? "pl-1" : ""}`}>
          <div className="h-1.5 flex-1 bg-gray-200 dark:bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${percentage}%` }}
            />
          </div>
          <span className="text-xs text-neutral-500 whitespace-nowrap">
            {completed}/{total}
          </span>
        </div>
      )}

      {/* ─── Bulk action bar (work-page-fixes Item 2) ───────────────────────
          Appears when one or more subtasks are selected. Styled like the
          main task list's selection bar in TaskListView — compact, subtle
          gray background, border-y, detached from the rows. Shows "{N}
          selected" on the left and a Delete button on the right with
          confirm-on-second-click (first click arms → "Delete?" in danger
          red, second click executes the bulk deletion). */}
      {selectedSubtaskIds.length > 0 && (
        <div className={`flex items-center gap-2 ${compact ? "px-2 py-1.5" : "px-4 py-2"} border-y border-gray-200 dark:border-gray-700/50 bg-gray-100 dark:bg-gray-800/50 text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1`}>
          <span className="flex-1">
            {selectedSubtaskIds.length} selected
          </span>
          <button
            type="button"
            disabled={bulkDeleting}
            onClick={bulkDeleteArmed ? handleBulkDelete : () => setBulkDeleteArmed(true)}
            className={`text-sm px-2 py-0.5 rounded disabled:opacity-50 ${
              bulkDeleteArmed
                ? "text-red-600 dark:text-red-400 font-bold"
                : "text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
            }`}
            title={bulkDeleteArmed ? "Click again to confirm" : "Delete selected subtasks"}
            aria-label={bulkDeleteArmed ? "Confirm bulk delete" : "Delete selected subtasks"}
          >
            {bulkDeleteArmed ? "Delete?" : "Delete"}
          </button>
        </div>
      )}

      <div className={!compact ? "space-y-1 pl-5" : "space-y-0.5 pl-1"}>
        {loading && (
          <div className="space-y-1.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-4 w-3/4 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
            ))}
          </div>
        )}
        {error && (
          <p className="text-xs text-red-500 dark:text-red-400">{error}</p>
        )}
        {!loading && !error && subtasks.length === 0 && (
          <p className={`text-sm text-gray-400 dark:text-gray-500 ${compact ? "px-1 py-0.5" : "px-2 py-1.5"}`}>
            No subtasks yet.
          </p>
        )}
        {!loading && !error && subtasks.map((st) => {
          const rowError = deleteErrors[st.id];
          const isArmed = deletingId === st.id;
          return (
            <div key={st.id}>
              <div className={`group/py flex items-center gap-2 rounded ${compact ? "py-0.5 px-1" : "py-1.5 px-2"} hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors`}>
                {/* Selection checkbox — bulk-select toggle for subtasks. Smaller
                    than the done circle (w-3.5 h-3.5 vs w-5 h-5) and uses the same
                    outlined-when-unchecked / filled-accent-when-checked pattern as
                    the task row checkboxes in InboxRow (#32B1FF accent, white
                    checkmark, no solid white fill). The row's gap-2 keeps it
                    visually separate from the done circle to its right. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleSubtaskSelect(st.id); }}
                  className="flex-shrink-0 flex items-center justify-center p-0 cursor-pointer focus:outline-none"
                  aria-label={isSubtaskSelected(st.id) ? "Deselect step for bulk action" : "Select step for bulk action"}
                  aria-pressed={isSubtaskSelected(st.id)}
                  title={isSubtaskSelected(st.id) ? "Deselect" : "Select for bulk action"}
                >
                  {isSubtaskSelected(st.id) ? (
                    // Checked — filled accent box (#32B1FF matches the task row
                    // checkboxes) with a white checkmark inside.
                    <svg className="w-3.5 h-3.5 text-[#32B1FF]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="3" fill="currentColor" />
                      <path d="M8.5 12.5l2.5 2.5 4.5-5.5" stroke="white" />
                    </svg>
                  ) : (
                    // Unchecked — muted gray outline, transparent background.
                    <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleToggleDone(st); }}
                  className="flex-shrink-0 flex items-center justify-center p-0 cursor-pointer focus:outline-none"
                  aria-label={st.done || st.completed_at ? "Mark step not done" : "Mark step done"}
                  title={st.done || st.completed_at ? "Mark not done" : "Mark done"}
                >
                  {st.done || st.completed_at ? (
                    // Done — filled green circle with white checkmark (16px dot,
                    // 10px checkmark — scaled down from 20px/12px for the compact
                    // subtask row; smaller than a main task row dot but still
                    // comfortably clickable.)
                    <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                      <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  ) : (
                    // Not done — outlined gray circle, transparent background (16px)
                    <span className="w-4 h-4 rounded-full border-2 border-gray-500 dark:border-gray-500 transition-colors" />
                  )}
                </button>
                {editingId === st.id ? (
                  <input
                    ref={editInputRef}
                    type="text"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onKeyDown={(e) => handleEditKeyDown(e, st)}
                    onBlur={() => handleEditBlur(st)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 min-w-0 text-sm px-0.5 py-0 bg-transparent rounded-sm border-b border-blue-400 dark:border-blue-500 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-300 dark:focus:ring-blue-700"
                    title="Enter to save · Esc to cancel"
                  />
                ) : (
                  <span
                    onClick={(e) => { e.stopPropagation(); handleStartEdit(st); }}
                    className={`flex-1 min-w-0 cursor-text text-sm truncate rounded-sm px-0.5 -mx-0.5 hover:bg-gray-100 dark:hover:bg-gray-800 ${st.done || st.completed_at ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-300"}`}
                    title="Click to rename"
                  >
                    {st.title || "Untitled"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleDeleteClick(st)}
                  className={`flex-shrink-0 text-xs font-medium transition-colors ${
                    isArmed
                      ? "text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      : "text-gray-400 dark:text-gray-500 opacity-0 group-hover/py:opacity-100 hover:text-red-500 dark:hover:text-red-400"
                  }`}
                  title={isArmed ? "Click again to confirm" : "Delete subtask"}
                  aria-label={isArmed ? "Confirm delete subtask" : "Delete subtask"}
                >
                  {isArmed ? (
                    "Confirm?"
                  ) : (
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  )}
                </button>
              </div>
              {rowError && (
                <p className="ml-4 mb-1 text-xs text-red-500 dark:text-red-400">{rowError}</p>
              )}
              {renameErrors[st.id] && (
                <p className="ml-4 mb-1 text-xs text-red-500 dark:text-red-400">{renameErrors[st.id]}</p>
              )}
              {doneErrors[st.id] && (
                <p className="ml-4 mb-1 text-xs text-red-500 dark:text-red-400">{doneErrors[st.id]}</p>
              )}
            </div>
          );
        })}
        {/* ─── "+ Add step" inline row ────────────────────────────────
            Subtle input + button that blends with the subtask rows above.
            Enter or + click calls createWork directly; on success the new
            subtask appends to the local array and the input re-focuses. */}
        {!loading && !error && (
          <div className={`flex items-center gap-2 ${compact ? "py-0.5 px-1 mt-0.5" : "py-1.5 px-2 mt-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800/50"} transition-colors`}>
            <button
              type="button"
              onClick={submitNewStep}
              disabled={submittingStep || !newStepTitle.trim()}
              className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Add step"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </button>
            <input
              ref={newStepRef}
              type="text"
              value={newStepTitle}
              onChange={(e) => setNewStepTitle(e.target.value)}
              onKeyDown={handleNewStepKeyDown}
              placeholder="Add a step..."
              className="flex-1 min-w-0 text-sm text-gray-500 dark:text-gray-400 placeholder-gray-400 dark:placeholder-gray-500 bg-transparent border-b border-gray-200 dark:border-gray-700 focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 py-0.5"
            />
          </div>
        )}
        {newStepError && (
          <p className="ml-5 mb-1 text-xs text-red-500 dark:text-red-400">{newStepError}</p>
        )}
      </div>
    </div>
  );
};

export default SubtaskSection;
